import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { Button } from './ui/button';
import {
  Settings,
  QrCode,
  Calendar as CalendarIcon,
  Building2,
  User,
  LogOut,
  Clock,
  MapPin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

export const CompanyDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useLanguage();
  const companyName = user?.company_name || user?.name || t('home.firma');
  const representativeName = user?.representative_first_name && user?.representative_last_name 
    ? `${user.representative_first_name} ${user.representative_last_name}` 
    : t('dashboard.vertreter');

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="pt-24 sm:pt-28 md:pt-32 pb-12 sm:pb-20 px-3 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header mit Company-Info */}
          <div className="bg-ngbc-beige rounded-2xl shadow-lg p-8 mb-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-gradient-to-br from-ngbc-primary to-ngbc-accent rounded-full flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-ngbc-dark">{companyName}</h1>
                  <p className="text-ngbc-muted text-sm">{t('dashboard.vertretenDurch')} {representativeName}</p>
                  <p className="text-ngbc-dark-text">{t('dashboard.willkommenZurueck')}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {(user?.is_admin || user?.is_manager || user?.is_ticket_controller) && (
                  <Button
                    onClick={() => navigate('/admin')}
                    variant="outline"
                    className="flex items-center gap-2 border-red-500 text-red-600 hover:bg-red-50"
                  >
                    <Settings className="w-4 h-4" />
                    {user?.is_ticket_controller && !user?.is_admin && !user?.is_manager ? t('dashboard.ticketPruefen') : t('header.adminPanel')}
                  </Button>
                )}
                <Button
                  onClick={() => {
                    logout();
                    navigate('/');
                  }}
                  variant="outline"
                  className="flex items-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  {t('dashboard.abmelden')}
                </Button>
              </div>
            </div>
          </div>

          {/* Dashboard Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Settings */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center">
                  <Settings className="w-6 h-6 text-ngbc-primary" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.einstellungen')}</h3>
              </div>
              <p className="text-ngbc-dark-text mb-4">
                {t('dashboard.einstellungenCompanyDesc')}
              </p>
              <Button className="w-full bg-ngbc-primary hover:bg-ngbc-accent">
                {t('dashboard.oeffnen')}
              </Button>
            </div>

            {/* QR Code */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center">
                  <QrCode className="w-6 h-6 text-ngbc-primary" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.qrCode')}</h3>
              </div>
              <p className="text-ngbc-dark-text mb-4">
                {t('dashboard.qrCodeDesc')}
              </p>
              <div className="bg-ngbc-secondary/50 p-8 rounded-lg mb-4 flex items-center justify-center">
                <div className="w-32 h-32 bg-ngbc-beige border-2 border-dashed border-ngbc-dark-text rounded-lg flex items-center justify-center">
                  <QrCode className="w-16 h-16 text-ngbc-muted" />
                </div>
              </div>
              <Button className="w-full bg-ngbc-primary hover:bg-ngbc-accent">
                {t('dashboard.qrCodeAnzeigen')}
              </Button>
            </div>

            {/* Zeitplan */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/30 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="w-6 h-6 text-ngbc-primary" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.firmenZeitplan')}</h3>
              </div>
              <p className="text-ngbc-dark-text mb-4">
                {t('dashboard.firmenZeitplanDesc')}
              </p>
              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm text-ngbc-dark-text">
                  <Clock className="w-4 h-4" />
                  <span>09:00 - Eröffnungskeynote</span>
                </div>
                <div className="flex items-center space-x-2 text-sm text-ngbc-dark-text">
                  <Clock className="w-4 h-4" />
                  <span>10:30 - Panel Diskussion</span>
                </div>
              </div>
              <Button className="w-full bg-ngbc-primary hover:bg-ngbc-accent">
                Zeitplan anzeigen
              </Button>
            </div>

            {/* Platzhalter 1 */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/50 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-ngbc-muted" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.platzhalter')} 1</h3>
              </div>
              <p className="text-ngbc-dark-text">
                {t('dashboard.weitereFunktionen')}
              </p>
            </div>

            {/* Platzhalter 2 */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/50 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-ngbc-muted" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">Platzhalter 2</h3>
              </div>
              <p className="text-ngbc-dark-text">
                {t('dashboard.weitereFunktionen')}
              </p>
            </div>

            {/* Platzhalter 3 */}
            <div className="bg-ngbc-beige rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer border-2 border-ngbc-secondary hover:border-ngbc-primary">
              <div className="flex items-center space-x-4 mb-4">
                <div className="w-12 h-12 bg-ngbc-secondary/50 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-ngbc-muted" />
                </div>
                <h3 className="text-xl font-bold text-ngbc-dark">{t('dashboard.platzhalter')} 3</h3>
              </div>
              <p className="text-ngbc-dark-text">
                {t('dashboard.weitereFunktionen')}
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};
